import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Grid, Stack, TextField, Typography,
} from '@mui/material';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import EmailIcon from '@mui/icons-material/Email';
import DownloadIcon from '@mui/icons-material/Download';
import VerifiedIcon from '@mui/icons-material/Verified';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { patient } from '@/api/endpoints';
import { formatDate, formatDateTime } from '@/utils/format';
import type { Prescription, PrescriptionItem, PrescriptionStatus } from '@/types/medical';

/**
 * Prescription detail.
 *
 * The view is split into:
 *   - Header card    — issuance metadata, status, signing fingerprint
 *   - Items list     — every drug with full dose / qty / refills / notes
 *   - Side panel     — actions (QR, share with pharmacy, download PDF)
 *                      — registry status (with the external_id when present)
 *
 * The QR is fetched lazily — only when the user clicks "Show QR" — so the
 * audit trail records a clear `downloaded` event for each look at the QR.
 */

export function PatientPrescriptionDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();

  const [qrOpen, setQrOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState('');

  const q = useApiQuery({
    queryKey: ['patient', 'prescriptions', id],
    queryFn:  () => patient.prescriptions.get(id),
    enabled:  Boolean(id),
  });

  const qrQ = useApiQuery({
    queryKey: ['patient', 'prescriptions', id, 'qr'],
    queryFn:  () => patient.prescriptions.qr(id),
    enabled:  Boolean(id) && qrOpen,
  });

  const pdfMut = useApiMutation({
    mutationFn: () => patient.prescriptions.pdf(id),
    onSuccess: (data) => {
      if (data.url) {
        window.open(data.url, '_blank');
      } else {
        toast.info('Generating PDF — try again in a few seconds.');
      }
    },
    onError: (e) => toast.error(e.message),
  });

  const shareMut = useApiMutation({
    mutationFn: (email: string) => patient.prescriptions.sharePharmacy(id, email),
    onSuccess: () => {
      toast.success(`Sent a secure link to ${shareEmail}.`);
      setShareOpen(false);
      setShareEmail('');
      qc.invalidateQueries({ queryKey: ['patient', 'prescriptions', id] });
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader
        title="Prescription"
        breadcrumbs={[
          { label: 'My prescriptions', to: '/patient/prescriptions' },
          { label: 'Detail' },
        ]}
      />

      <DataState query={q}>
        {(rx) => (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <HeaderCard rx={rx} />
              <ItemsCard items={rx.items ?? []} />
              {rx.safety_check_snapshot && 'overall_severity' in rx.safety_check_snapshot && (
                <SafetyCard snapshot={rx.safety_check_snapshot as { overall_severity: string }} />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <ActionsCard
                rx={rx}
                onShowQR={() => setQrOpen(true)}
                onShare={() => setShareOpen(true)}
                onDownload={() => pdfMut.mutate()}
                downloadBusy={pdfMut.isPending}
              />
              <RegistryCard rx={rx} />
            </Grid>
          </Grid>
        )}
      </DataState>

      {/* QR dialog */}
      <Dialog open={qrOpen} onClose={() => setQrOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Show this at the pharmacy</DialogTitle>
        <DialogContent>
          <DataState query={qrQ}>
            {(qr) => (
              <Stack alignItems="center" spacing={2}>
                <Box sx={{
                  width: 240, height: 240,
                  bgcolor: 'background.subtle',
                  borderRadius: 2,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'text.secondary',
                  textAlign: 'center', p: 2,
                }}>
                  <Stack alignItems="center" spacing={1}>
                    <QrCode2Icon sx={{ fontSize: 80 }} />
                    <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                      {qr.qr_payload?.slice(0, 60)}…
                    </Typography>
                  </Stack>
                </Box>
                {qr.registry_external_id && (
                  <Typography variant="body2" align="center">
                    Registry id:{' '}
                    <Box component="span" className="tabular" fontWeight={600}>
                      {qr.registry_external_id}
                    </Box>
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" align="center">
                  The pharmacy scans this to fetch your prescription securely.
                  No medication data is stored in this code itself.
                </Typography>
              </Stack>
            )}
          </DataState>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={shareOpen} onClose={() => setShareOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Share with pharmacy</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            We'll email a one-time secure link the pharmacy can use to
            fetch this prescription. The email itself contains no
            medication details.
          </Typography>
          <TextField
            autoFocus fullWidth label="Pharmacy email" type="email"
            value={shareEmail} onChange={(e) => setShareEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!shareEmail.includes('@') || shareMut.isPending}
            onClick={() => shareMut.mutate(shareEmail.trim())}
          >
            Send link
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function HeaderCard({ rx }: { rx: Prescription }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="overline" color="text.secondary">Prescription</Typography>
            <Typography variant="h5" component="h2" sx={{ mb: 1 }} className="tabular">
              {rx.registry_external_id || rx.id.slice(0, 8)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Issued by <strong>{rx.clinician_name}</strong>
              {rx.issued_at && <> on {formatDateTime(rx.issued_at)}</>}
              <> · jurisdiction {rx.jurisdiction_code}</>
            </Typography>
            {rx.valid_until && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Valid until <span className="tabular">{formatDate(rx.valid_until)}</span>
              </Typography>
            )}
          </Box>
          <StatusChip status={rx.status} />
        </Stack>
        {rx.signing_key_fingerprint_at_issue && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <VerifiedIcon color="success" fontSize="small" />
              <Typography variant="caption" color="text.secondary">
                Signed with key fingerprint{' '}
                <Box component="span" className="tabular">
                  {rx.signing_key_fingerprint_at_issue}
                </Box>
              </Typography>
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function ItemsCard({ items }: { items: PrescriptionItem[] }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Medications ({items.length})
        </Typography>
        <Stack divider={<Divider />} spacing={1.5}>
          {items.map((it) => (
            <Box key={it.id}>
              <Typography variant="subtitle1" fontWeight={600}>
                {it.drug_name_snapshot}
                {it.drug_strength_snapshot && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {' '}· {it.drug_strength_snapshot}
                  </Typography>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {it.drug_form_snapshot} · ATC {it.atc_snapshot}
              </Typography>
              <Stack direction="row" spacing={3} sx={{ mt: 1, flexWrap: 'wrap' }}>
                <Field label="Dose" value={it.dose_text || '—'} />
                <Field label="Quantity" value={it.quantity_text || '—'} />
                <Field label="Refills" value={String(it.refills)} />
                {it.indication_icd_code && (
                  <Field label="ICD" value={it.indication_icd_code} />
                )}
              </Stack>
              {it.notes_to_patient && (
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'background.subtle', borderRadius: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    For you:
                  </Typography>{' '}
                  <Typography variant="body2" component="span">{it.notes_to_patient}</Typography>
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" className="tabular" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  );
}

function SafetyCard({ snapshot }: { snapshot: { overall_severity: string } }) {
  if (!snapshot?.overall_severity || snapshot.overall_severity === 'none') return null;
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          Safety check
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Your clinician reviewed and accepted this prescription despite a{' '}
          <Box component="span" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
            {snapshot.overall_severity}
          </Box>{' '}
          interaction warning. Ask them if you have questions.
        </Typography>
      </CardContent>
    </Card>
  );
}

function ActionsCard({
  rx, onShowQR, onShare, onDownload, downloadBusy,
}: {
  rx: Prescription;
  onShowQR: () => void;
  onShare: () => void;
  onDownload: () => void;
  downloadBusy: boolean;
}) {
  const dispensable = ['signed', 'registered', 'partly_dispensed'].includes(rx.status);
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary"
                    sx={{ display: 'block', mb: 1.5 }}>
          Actions
        </Typography>
        <Stack spacing={1}>
          <Button
            variant="contained" fullWidth disabled={!dispensable}
            startIcon={<QrCode2Icon />} onClick={onShowQR}
          >
            Show pharmacy QR
          </Button>
          <Button
            variant="outlined" fullWidth disabled={!dispensable}
            startIcon={<EmailIcon />} onClick={onShare}
          >
            Share with pharmacy
          </Button>
          <Button
            variant="outlined" fullWidth
            startIcon={<DownloadIcon />} onClick={onDownload}
            disabled={downloadBusy}
          >
            Download PDF
          </Button>
        </Stack>
        {!dispensable && (
          <Typography variant="caption" color="text.secondary"
                      sx={{ display: 'block', mt: 1.5 }}>
            This prescription is not currently dispensable
            ({rx.status.replace(/_/g, ' ')}).
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function RegistryCard({ rx }: { rx: Prescription }) {
  if (!rx.registry_code && !rx.registry_failure_reason) return null;
  return (
    <Card>
      <CardContent>
        <Typography variant="overline" color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}>
          Registry
        </Typography>
        <Stack spacing={0.5}>
          <Typography variant="body2">
            <strong>Code:</strong> {rx.registry_code || '—'}
          </Typography>
          {rx.registry_external_id && (
            <Typography variant="body2">
              <strong>ID:</strong>{' '}
              <Box component="span" className="tabular">{rx.registry_external_id}</Box>
            </Typography>
          )}
          {rx.registry_submitted_at && (
            <Typography variant="caption" color="text.secondary">
              Registered {formatDateTime(rx.registry_submitted_at)}
            </Typography>
          )}
          {rx.registry_failure_reason && (
            <Typography variant="body2" color="error">
              Registry returned: {rx.registry_failure_reason}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function StatusChip({ status }: { status: PrescriptionStatus }) {
  const m: Record<PrescriptionStatus, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }> = {
    draft:           { label: 'draft', color: 'default' },
    signed:          { label: 'active', color: 'primary' },
    registered:      { label: 'registered', color: 'primary' },
    partly_dispensed:{ label: 'partly filled', color: 'primary' },
    dispensed:       { label: 'dispensed', color: 'success' },
    cancelled:       { label: 'cancelled', color: 'default' },
    expired:         { label: 'expired', color: 'default' },
    failed:          { label: 'failed', color: 'error' },
  };
  const c = m[status];
  return <Chip label={c.label} color={c.color} variant="outlined" />;
}
